import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Users,
  ScrollText,
  Swords,
  MapPin,
  Map,
  BookOpen,
  UserCheck,
  Archive,
  CircleDot,
  HelpCircle,
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, variant }) => (
  <Card>
    <CardContent className="flex items-center gap-4 p-4">
      <div className={`p-2 rounded-lg ${variant || 'bg-primary/10'}`}>
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboardPage = () => {
  const { language } = useAuth();
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, healthRes] = await Promise.all([
          axios.get('/api/admin/stats'),
          axios.get('/api/admin/system/health'),
        ]);
        setStats(statsRes.data);
        setHealth(healthRes.data);
      } catch (err) {
        console.error('Failed to load admin stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse text-muted-foreground">{t('loading', language)}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-cinzel">{t('admin_dashboard', language)}</h1>
          {health && (
            <div className="mt-2 flex gap-2">
              <Badge variant={health.mongodb?.status === 'online' ? 'default' : 'destructive'}>
                MongoDB: {health.mongodb?.status === 'online' ? t('admin_service_online', language) : t('admin_service_offline', language)}
              </Badge>
            </div>
          )}
        </div>

        {stats && (
          <>
            {/* Users & Campaigns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label={t('admin_total_users', language)} value={stats.users_total} />
              <StatCard icon={UserCheck} label={t('admin_active_users', language)} value={stats.users_active} />
              <StatCard icon={ScrollText} label={t('admin_active_campaigns', language)} value={stats.campaigns_active} />
              <StatCard icon={Archive} label={t('admin_archived_campaigns', language)} value={stats.campaigns_archived} />
            </div>

            {/* Content stats */}
            <h2 className="text-lg font-semibold mt-6">{t('admin_platform_content', language)}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={CircleDot} label={t('admin_total_sessions', language)} value={stats.sessions_total} />
              <StatCard icon={HelpCircle} label={t('admin_total_quests', language)} value={stats.quests_total} />
              <StatCard icon={Users} label={t('admin_total_npcs', language)} value={stats.npcs_total} />
              <StatCard icon={MapPin} label={t('admin_total_locations', language)} value={stats.locations_total} />
              <StatCard icon={Map} label={t('admin_total_maps', language)} value={stats.maps_total} />
              <StatCard icon={Swords} label={t('admin_total_encounters', language)} value={stats.encounters_total} />
              <StatCard icon={BookOpen} label={t('admin_total_lore', language)} value={stats.lore_total} />
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
